import React, { useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import moment from "moment";
import { get } from "lodash";
import { DB_DATE_FORMAT, DB_HOURS_FORMAT } from "../../../constants";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// --- Helper Functions & Components ---

const getBarColor = (reservation) => {
  const { reservation_notes, order_details } = reservation;

  if (reservation_notes?.table?.isExclusive) return "#673ab7";

  const guestCount = order_details
    ? order_details
        .filter((d) => d.item?.type === "reservation_experience")
        .reduce((acc, cur) => acc + (cur.qty || 0), 0)
    : reservation.guest_count || 0;

  if (guestCount <= 2) return "#4CAF50"; // Green
  if (guestCount <= 4) return "#2196F3"; // Blue
  if (guestCount <= 6) return "#FFC107"; // Yellow
  if (guestCount <= 8) return "#FF9800"; // Orange
  return "#F44336"; // Red for 9+
};

const Legend = () => {
  const legendItems = [
    { color: "#4CAF50", label: "1-2 Pax" },
    { color: "#2196F3", label: "3-4 Pax" },
    { color: "#FFC107", label: "5-6 Pax" },
    { color: "#FF9800", label: "7-8 Pax" },
    { color: "#F44336", label: "9+ Pax" },
    { color: "#673ab7", label: "PDR" },
  ];
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        gap: "20px",
        marginTop: "10px",
      }}
    >
      {legendItems.map((item) => (
        <div key={item.label} style={{ display: "flex", alignItems: "center" }}>
          <div
            style={{
              width: "15px",
              height: "15px",
              backgroundColor: item.color,
              marginRight: "5px",
            }}
          ></div>
          <span>{item.label}</span>
        </div>
      ))}
    </div>
  );
};

// --- Main Component ---

const ServiceTimeline = ({
  reservations,
  onGoingService,
  store,
  selectedDate,
  headers,
  handleShowDatepickerModal,
  onCustomerNameClick,
}) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef();

  const handlePrint = async () => {
    const element = printRef.current;
    if (!element) return;

    // Find the scroll wrapper
    const scrollable = element.querySelector(".timeline-scroll-wrapper");
    if (!scrollable) return;

    setIsPrinting(true);

    // 1. Capture Original Styles
    const originalOverflow = scrollable.style.overflow;
    const originalWidth = scrollable.style.width;
    const originalFlex = scrollable.style.flex; // Capture flex just in case

    // 2. Calculate the Full Content Width
    // scrollWidth gives us the total width of the content, including hidden parts
    const totalContentWidth = scrollable.scrollWidth;

    // Calculate total width needed for the screenshot (Sidebar + Scrollable Content)
    // We add a buffer (e.g., 150px) to account for the Y-Axis sidebar width
    const totalScreenshotWidth = totalContentWidth + 150;

    // 3. Apply Temporary Styles to Expand Content
    // We force the scrollable area to be its full pixel width
    scrollable.style.width = `${totalContentWidth}px`;
    scrollable.style.overflow = "visible";
    scrollable.style.flex = "none"; // Disable flex shrinking

    try {
      const canvas = await html2canvas(element, {
        useCORS: true,
        scale: 2, // High resolution
        // CRITICAL FIXES BELOW:
        width: totalScreenshotWidth, // The width of the final canvas
        windowWidth: totalScreenshotWidth, // Simulate a very wide browser window
        x: 0, // Force capture to start at x=0
        scrollX: 0, // Ensure no horizontal scroll offset is applied
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("l", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = imgWidth / imgHeight;

      const widthInPdf = pdfWidth - 20;
      const heightInPdf = widthInPdf / ratio;

      // Logic to ensure it fits on one page (landscape)
      if (heightInPdf > pdfHeight - 20) {
        // If height is still too big, scale based on height instead
        const fitHeight = pdfHeight - 20;
        const fitWidth = fitHeight * ratio;
        pdf.addImage(imgData, "PNG", 10, 10, fitWidth, fitHeight);
      } else {
        pdf.addImage(imgData, "PNG", 10, 10, widthInPdf, heightInPdf);
      }

      pdf.save(`service-chart-${selectedDate}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      // 4. Restore Original Styles
      scrollable.style.overflow = originalOverflow;
      scrollable.style.width = originalWidth;
      scrollable.style.flex = originalFlex;
      setIsPrinting(false);
    }
  };
  // 1. Logic & Data Processing
  const { series, chartHeight, minTime, maxTime, hourlyData } = useMemo(() => {
    const processedReservations = reservations
      .map((res) => {
        const dateStr = res.order_date || selectedDate;
        const startTime = moment.utc(
          `${dateStr} ${res.order_time}`,
          `${DB_DATE_FORMAT} ${DB_HOURS_FORMAT}`,
        );
        const diningLength =
          res.reservation_notes?.dining_service?.interval || 60;
        const endTime = startTime.clone().add(diningLength, "minutes");
        return {
          ...res,
          startTime,
          endTime,
          startTs: startTime.valueOf(),
          endTs: endTime.valueOf(),
        };
      })
      .sort((a, b) => a.startTs - b.startTs);

    const dayOfWeek = moment.utc(selectedDate, DB_DATE_FORMAT).format("d");
    const storeHoursToday = store?.store_hours?.find(
      (h) => h.day === dayOfWeek && h.isOpen,
    );
    let axisMin, axisMax;
    if (storeHoursToday) {
      axisMin = moment
        .utc(
          `${selectedDate} ${storeHoursToday.opening}`,
          `${DB_DATE_FORMAT} HH:mm`,
        )
        .subtract(60, "minutes")
        .valueOf();
      axisMax = moment
        .utc(
          `${selectedDate} ${storeHoursToday.closing}`,
          `${DB_DATE_FORMAT} HH:mm`,
        )
        .add(60, "minutes")
        .valueOf();
    } else {
      axisMin = moment
        .utc(selectedDate, DB_DATE_FORMAT)
        .startOf("day")
        .add(8, "hours")
        .valueOf();
      axisMax = moment.utc(selectedDate, DB_DATE_FORMAT).endOf("day").valueOf();
    }

    if (processedReservations.length > 0) {
      const earliest = Math.min(...processedReservations.map((r) => r.startTs));
      const latest = Math.max(...processedReservations.map((r) => r.endTs));
      if (earliest < axisMin) axisMin = earliest - 60 * 60 * 1000;
      if (latest > axisMax) axisMax = latest + 60 * 60 * 1000;
    }

    // Timeline Data
    const uniqueTables = [
      ...new Set(
        processedReservations.map(
          (r) => r.reservation_notes?.table?.name || "Unassigned",
        ),
      ),
    ].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    );
    const dataPoints = [];
    let totalRows = 0;

    uniqueTables.forEach((tableName) => {
      const tableReservations = processedReservations.filter(
        (r) => (r.reservation_notes?.table?.name || "Unassigned") === tableName,
      );
      const lanes = [];
      tableReservations.forEach((res) => {
        let placed = false;
        for (let i = 0; i < lanes.length; i++) {
          if (!res.startTime.isBefore(lanes[i].lastEndTime)) {
            lanes[i].reservations.push(res);
            lanes[i].lastEndTime = res.endTime;
            placed = true;
            break;
          }
        }
        if (!placed)
          lanes.push({ lastEndTime: res.endTime, reservations: [res] });
      });
      totalRows += lanes.length;
      lanes.forEach((lane, laneIndex) => {
        const yLabel =
          laneIndex === 0 ? tableName : `${tableName} (${laneIndex + 1})`;
        lane.reservations.forEach((res) => {
          const guestName = `${res.guest_customer?.first_name || ""} ${
            res.guest_customer?.last_name || ""
          }`;
          const guestCount = res.order_details
            ? res.order_details
                .filter((d) => d.item?.type === "reservation_experience")
                .reduce((acc, cur) => acc + (cur.qty || 0), 0)
            : res.guest_count || 0;
          const price =
            res.payment?.amount > 0 ? `${res.payment.amount} Php` : "";
          dataPoints.push({
            x: yLabel,
            y: [res.startTs, res.endTs],
            fillColor: getBarColor(res),
            meta: {
              guestName,
              guestCount,
              notes: res.notes,
              price,
              reservation: res,
            },
            customLabel: `(${guestCount}) ${guestName}${
              price ? " - " + price : ""
            }`,
          });
        });
      });
    });
    const calculatedHeight = Math.max(250, totalRows * 32);

    // Hourly aggregation for bar chart
    const hourlyAgg = {};
    processedReservations.forEach((res) => {
      const startHour = moment
        .utc(res.startTime)
        .startOf("hour")
        .format("HH:00");
      const guestCount = res.order_details
        ? res.order_details
            .filter((d) => d.item?.type === "reservation_experience")
            .reduce((acc, cur) => acc + (cur.qty || 0), 0)
        : res.guest_count || 0;
      if (!hourlyAgg[startHour]) hourlyAgg[startHour] = 0;
      hourlyAgg[startHour] += guestCount;
    });

    const allHours = [];
    let currentHour = moment.utc(axisMin).startOf("hour");
    const endHour = moment.utc(axisMax).startOf("hour");

    while (currentHour.isSameOrBefore(endHour)) {
      allHours.push(currentHour.format("HH:00"));
      currentHour.add(1, "hour");
    }

    const hoursSorted = allHours.sort();
    const hourlyData = {
      categories: hoursSorted,
      series: hoursSorted.map((h) => hourlyAgg[h] || 0),
    };

    return {
      series: [{ name: "Reservations", data: dataPoints }],
      chartHeight: calculatedHeight,
      minTime: axisMin,
      maxTime: axisMax,
      hourlyData,
    };
  }, [reservations, store, selectedDate, onGoingService]);

  // 2. Chart Configurations
  const timelineOptions = {
    chart: {
      type: "rangeBar",
      fontFamily: "inherit",
      toolbar: { show: false },
      animations: { enabled: false }, // Must be false for printing
      zoom: { enabled: false },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          const { dataPointIndex, seriesIndex } = config;
          const reservation =
            config.w.config.series[seriesIndex].data[dataPointIndex].meta
              .reservation;
          if (onCustomerNameClick && reservation)
            onCustomerNameClick(reservation);
        },
      },
      parentHeightOffset: 0,
    },
    stroke: {
      show: true,
      width: 1,
      colors: ["#fff"],
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "90%",
        borderRadius: 2,
        rangeBarGroupRows: true,
      },
    },
    dataLabels: {
      enabled: true,
      textAnchor: "middle",
      formatter: (val, opts) =>
        opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex]
          .customLabel || "",
      style: { colors: ["#fff"], fontSize: "10px", fontWeight: 600 },
      offsetX: 2,
      background: { enabled: false },
    },
    xaxis: {
      type: "datetime",
      position: "top",
      min: minTime,
      max: maxTime,
      tickAmount: Math.floor((maxTime - minTime) / (1000 * 60 * 30)),
      tickPlacement: "between",
      forceNiceScale: false,
      labels: {
        formatter: (v) =>
          moment.utc(v).minute() === 0 ? moment.utc(v).format("HH:mm") : "",
        style: { fontSize: "11px", colors: "#9aa0ac" },
        datetimeUTC: false,
      },
      axisTicks: { show: true },
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: {
        style: { colors: "#404040", fontSize: "11px", fontWeight: 600 },
        align: "right",
        minWidth: 80,
      },
    },
    grid: {
      show: true,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
      row: { colors: ["#f8f9fa", "transparent"], opacity: 0.6 },
      padding: { top: 0, bottom: 0, left: 10, right: 0 },
    },
    annotations: {
      xaxis: [
        {
          x: moment().valueOf(),
          borderColor: "#FF4560",
          strokeDashArray: 0,
          label: {
            style: { color: "#fff", background: "#FF4560", fontSize: "10px" },
            text: "Now",
          },
        },
      ],
    },
    tooltip: {
      custom: ({ seriesIndex, dataPointIndex, w }) => {
        const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
        const start = moment.utc(data.y[0]).format("HH:mm");
        const end = moment.utc(data.y[1]).format("HH:mm");
        const name = data.meta?.guestName || "Guest";
        const pax = data.meta?.guestCount || 0;
        const price = data.meta?.price ? `• ${data.meta.price}` : "";
        return `<div style="padding:8px 12px;border:1px solid #ddd;background:#fff;border-radius:4px;font-size:11px;box-shadow:0 2px 4px rgba(0,0,0,0.1);">
                <div style="font-weight:700;margin-bottom:2px;">${name} <span style="font-weight:400;color:#666;">(${pax} Pax)</span></div>
                <div>${start} - ${end} ${price}</div>
              </div>`;
      },
    },
  };
  const labelWidth = 110;
  const yAxisOptions = {
    chart: {
      type: "rangeBar",
      toolbar: { show: false },
      animations: { enabled: false },
    },
    plotOptions: { bar: { horizontal: true } },
    xaxis: {
      type: "datetime",
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      ...timelineOptions.yaxis,
      labels: {
        ...timelineOptions.yaxis.labels,
        minWidth: labelWidth,
        maxWidth: labelWidth,
      },
    },
    grid: { show: false },
  };

  const barOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      animations: { enabled: false },
    },
    colors: ["#9E9E9E"],
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    plotOptions: {
      bar: { horizontal: false, columnWidth: "75%", borderRadius: 2 },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: hourlyData.categories,
      labels: { style: { fontSize: "11px", colors: "#9aa0ac" } },
    },
    yaxis: { labels: { style: { fontSize: "11px", colors: "#727275" } } },
    tooltip: { y: { formatter: (val) => `${val} Pax` } },
  };

  const mainTimelineOptions = {
    ...timelineOptions,
    yaxis: { ...timelineOptions.yaxis, labels: { show: false } },
  };

  // 3. Render
  return (
    <>
      <div className="d-flex justify-content-end pb-2 d-print-none">
        <button
          className="print-button"
          onClick={handlePrint}
          disabled={isPrinting}
        >
          {isPrinting ? "Generating PDF..." : "Download Chart"}
        </button>
      </div>

      <div
        className="service-chart-container"
        style={{
          background: "#fff",
          borderRadius: "8px",
          border: "1px solid #e0e0e0",
          padding: "16px",
        }}
      >
        <style>
          {`
        /* --- Screen Styles --- */
        .print-button {
          padding: 8px 16px;
          color: #fff;
          font-size: 14px;
          font-weight: 500;
          border-radius: 4px;
          border: 1px solid #673ab7;
          background-color: #673ab7;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }
        .print-button:hover {
          background-color: #512da8; /* Darker purple on hover */
        }
        .print-button:disabled {
          background-color: #b39ddb; /* Lighter purple when disabled */
          border-color: #b39ddb;
          cursor: not-allowed;
        }
        
        /* Flex wrapper to side-by-side the axis and the timeline */
        .chart-flex-wrapper {
            display: flex;
            width: 100%;
            position: relative;
        }

        .print-button-wrapper {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 16px;
        }

        /* Y-Axis Column */
        .y-axis-wrapper {
           position: sticky;
           left: 0;
           z-index: 10;
           background: #fff;
           flex-shrink: 0;
        }

        /* Scrollable Timeline Column */
        .timeline-scroll-wrapper {
           overflow-x: auto;
           overflow-y: hidden;
           width: 100%;
           flex-grow: 1;
        }

        /* --- Print Specific Styles --- */
        @media print {
          @page {
            size: landscape;
            margin: 0;
          }

          /* Default: Hide everything */
          body * {
            visibility: hidden;
          }

          /* Force our specific component to show */
          .service-chart-container, 
          .service-chart-container * {
            visibility: visible;
          }

          /* FIX: Use 'fixed' positioning to break out of any hidden/collapsed parents 
             and cover the entire page with a white background.
          */
          .service-chart-container {
            position: fixed !important; 
            top: 0 !important;
            left: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            background: white !important;
            z-index: 99999 !important; /* Ensure it is on top */
            padding: 20px !important;
            margin: 0 !important;
            overflow: visible !important;
            display: block !important;
          }

          /* Hide the print button */
          .print-button {
            display: none !important;
          }

          /* Clean up the axis wrapper for print */
          .y-axis-wrapper {
             position: relative !important; 
             border-right: 1px solid #ccc;
             background: transparent !important;
          }
          
          /* Force timeline wrapper to display full width (no scrolling) */
          .timeline-scroll-wrapper {
            overflow: visible !important;
            display: block !important;
            width: auto !important;
            flex-grow: 0 !important;
          }

          /* SCALING: Fit the 2100px chart onto the paper.
             We scale ONLY the inner content, not the fixed container.
          */
          .printable-area {
            transform: scale(0.60); /* Shrink to fit paper */
            transform-origin: top left;
            width: 166% !important; /* Expand width to compensate for shrink */
            display: flex;
            align-items: flex-start;
            margin-top: 10px;
          }
        }
      `}
        </style>

        {/* Added class 'printable-area' for print targeting */}
        {/* The ref is attached to the element we want to convert to PDF */}
        <div className="printable-area" ref={printRef}>
          {headers}
          <div className="chart-flex-wrapper">
            {/* Y-Axis (Sticky on screen, relative on print) */}
            <div className="y-axis-wrapper">
              <Chart
                options={yAxisOptions}
                series={series}
                type="rangeBar"
                height={chartHeight}
                width={labelWidth}
              />
            </div>

            {/* Main Timeline (Scrollable on screen, visible on print) */}
            <div className="timeline-scroll-wrapper">
              <Chart
                style={{ minWidth: "2100px" }}
                options={mainTimelineOptions}
                series={series}
                type="rangeBar"
                height={chartHeight}
                width="2100px"
              />
            </div>
          </div>

          <Legend />

          <div style={{ marginTop: "20px", width: "100%" }}>
            <Chart
              options={barOptions}
              series={[{ name: "Pax", data: hourlyData.series }]}
              type="bar"
              height={200}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceTimeline;
